import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  LogOut, ArrowLeft, Save, Plus, Trash2, Eye,
  Brain, BookOpen, Award, MessageSquare, CheckCircle, Clock,
  GraduationCap, Star, Shield, Zap, Target, Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import LoadingQuip from "@/components/LoadingQuip";
import { useTheme } from "@/contexts/ThemeContext";
import DarkModeToggle from "@/components/DarkModeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon options for features and steps
const ICON_OPTIONS = [
  { value: "Brain", label: "Brain", icon: Brain },
  { value: "BookOpen", label: "Book", icon: BookOpen },
  { value: "Award", label: "Award", icon: Award },
  { value: "MessageSquare", label: "Message", icon: MessageSquare },
  { value: "CheckCircle", label: "Check", icon: CheckCircle },
  { value: "Clock", label: "Clock", icon: Clock },
  { value: "GraduationCap", label: "Graduation", icon: GraduationCap },
  { value: "Star", label: "Star", icon: Star },
  { value: "Shield", label: "Shield", icon: Shield },
  { value: "Zap", label: "Zap", icon: Zap },
  { value: "Target", label: "Target", icon: Target },
  { value: "Users", label: "Users", icon: Users },
];

const COLOR_OPTIONS = [
  { value: "#102862", label: "Navy Blue" },
  { value: "#9E3639", label: "Burgundy Red" },
  { value: "#FF6B35", label: "Orange" },
  { value: "#16A34A", label: "Green" },
  { value: "#7C3AED", label: "Purple" },
  { value: "#0891B2", label: "Cyan" },
];

function IconPreview({ iconName, className }) {
  const iconOption = ICON_OPTIONS.find(opt => opt.value === iconName);
  if (iconOption) {
    const IconComponent = iconOption.icon;
    return <IconComponent className={className} />;
  }
  return <Brain className={className} />;
}

export default function AdminLandingPage({ user, logout }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [content, setContent] = useState({
    hero_title: "",
    hero_subtitle: "",
    hero_button_text: "",
    hero_button_subtext: "",
    features_title: "",
    features: [],
    how_it_works_title: "",
    how_it_works_subtitle: "",
    steps: [],
    cta_title: "",
    cta_subtitle: "",
    cta_button_text: "",
    footer_text: ""
  });
  
  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/landing-page`);
      setContent(response.data);
    } catch (error) {
      toast.error("Error loading content");
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/landing-page`, content, { headers });
      toast.success("Landing page updated!");
    } catch (error) {
      toast.error("Error saving content");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  // Feature management
  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [...prev.features, { icon: "Brain", title: "", description: "", color: "#102862" }]
    }));
  };

  const updateFeature = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const removeFeature = (index) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Step management
  const addStep = () => {
    const nextNumber = content.steps.length + 1;
    setContent(prev => ({
      ...prev,
      steps: [...prev.steps, { step_number: nextNumber, icon: "BookOpen", title: "", description: "", color: "#102862" }]
    }));
  };

  const updateStep = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeStep = (index) => {
    setContent(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_number: i + 1 }))
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: isDarkMode ? '#092036' : '#F4F4F4' }}>
        <LoadingQuip size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: isDarkMode ? '#092036' : '#F4F4F4' }}>
      {/* Header */}
      <header className="shadow-sm sticky top-0 z-50" style={{ backgroundColor: isDarkMode ? '#092036' : 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="gap-2"
                style={{ color: isDarkMode ? '#e2e8f0' : undefined }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
              <span className="text-lg font-semibold" style={{ color: isDarkMode ? '#ffffff' : '#102862' }}>
                Edit Landing Page
              </span>
            </div>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('/', '_blank')}
                className="gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              <Button
                onClick={saveContent}
                disabled={saving}
                size="sm"
                className="gap-2 text-white"
                style={{ background: '#102862' }}
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              <Button variant="ghost" size="icon" onClick={logout}>
                <LogOut className="w-5 h-5" style={{ color: '#9E3639' }} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        
        {/* Hero Section */}
        <Card className="p-6 light-form-card" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
          <h2 className="text-xl font-bold mb-6" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
            Hero Section
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={content.hero_title}
                onChange={(e) => updateField('hero_title', e.target.value)}
                placeholder="Main headline"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Textarea
                value={content.hero_subtitle}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                placeholder="Supporting text under the headline"
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input
                  value={content.hero_button_text}
                  onChange={(e) => updateField('hero_button_text', e.target.value)}
                  placeholder="e.g., Get Started"
                />
              </div>
              <div>
                <Label>Button Subtext</Label>
                <Input
                  value={content.hero_button_subtext}
                  onChange={(e) => updateField('hero_button_subtext', e.target.value)}
                  placeholder="e.g., Takes less than 30 seconds"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Features Section */}
        <Card className="p-6 light-form-card" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
              Features Section
            </h2>
            <Button onClick={addFeature} size="sm" variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Add Feature
            </Button>
          </div>
          
          <div className="mb-4">
            <Label>Section Title</Label>
            <Input
              value={content.features_title}
              onChange={(e) => updateField('features_title', e.target.value)}
              placeholder="e.g., Why Choose Us?"
            />
          </div>
          
          <div className="space-y-4">
            {content.features?.map((feature, index) => (
              <div key={index} className="p-4 border rounded-lg bg-gray-50">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: feature.color }}
                    >
                      <IconPreview iconName={feature.icon} className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-medium">Feature {index + 1}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFeature(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Icon</Label>
                    <Select value={feature.icon} onValueChange={(v) => updateFeature(index, 'icon', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ICON_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="w-4 h-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Select value={feature.color} onValueChange={(v) => updateFeature(index, 'color', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLOR_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded" style={{ background: opt.value }} />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={feature.title}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                      placeholder="Feature title"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={feature.description}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                      placeholder="Feature description"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* How It Works Section */}
        <Card className="p-6 light-form-card" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
              How It Works Section
            </h2>
            <Button onClick={addStep} size="sm" variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Add Step
            </Button>
          </div>
          
          <div className="space-y-4 mb-6">
            <div>
              <Label>Section Title</Label>
              <Input
                value={content.how_it_works_title}
                onChange={(e) => updateField('how_it_works_title', e.target.value)}
                placeholder="e.g., How It Works"
              />
            </div>
            <div>
              <Label>Section Subtitle</Label>
              <Input
                value={content.how_it_works_subtitle}
                onChange={(e) => updateField('how_it_works_subtitle', e.target.value)}
                placeholder="Brief description"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            {content.steps?.map((step, index) => (
              <div key={index} className="p-4 border rounded-lg bg-gray-50">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <span 
                      className="text-sm font-bold px-3 py-1 rounded-full text-white"
                      style={{ background: step.color }}
                    >
                      Step {step.step_number}
                    </span>
                    <IconPreview iconName={step.icon} className="w-5 h-5" style={{ color: step.color }} />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStep(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Icon</Label>
                    <Select value={step.icon} onValueChange={(v) => updateStep(index, 'icon', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ICON_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="w-4 h-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Select value={step.color} onValueChange={(v) => updateStep(index, 'color', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLOR_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded" style={{ background: opt.value }} />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={step.title}
                      onChange={(e) => updateStep(index, 'title', e.target.value)}
                      placeholder="Step title"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={step.description}
                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                      placeholder="Step description"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* CTA Section */}
        <Card className="p-6 light-form-card" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
          <h2 className="text-xl font-bold mb-6" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
            Call-to-Action Section
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={content.cta_title}
                onChange={(e) => updateField('cta_title', e.target.value)}
                placeholder="CTA headline"
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input
                value={content.cta_subtitle}
                onChange={(e) => updateField('cta_subtitle', e.target.value)}
                placeholder="Supporting text"
              />
            </div>
            <div>
              <Label>Button Text</Label>
              <Input
                value={content.cta_button_text}
                onChange={(e) => updateField('cta_button_text', e.target.value)}
                placeholder="e.g., Start Learning Today"
              />
            </div>
          </div>
        </Card>

        {/* Footer Section */}
        <Card className="p-6 light-form-card" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
          <h2 className="text-xl font-bold mb-6" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
            Footer
          </h2>
          <div>
            <Label>Footer Text</Label>
            <Input
              value={content.footer_text}
              onChange={(e) => updateField('footer_text', e.target.value)}
              placeholder="e.g., © 2025 Company Name. All rights reserved."
            />
          </div>
        </Card>

        {/* Save Button at Bottom */}
        <div className="flex justify-end">
          <Button
            onClick={saveContent}
            disabled={saving}
            className="gap-2 text-white px-8"
            style={{ background: '#102862' }}
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save All Changes"}
          </Button>
        </div>
      </main>
    </div>
  );
}
